/**
 * I5 / R9 — site audit strings (fr = source; en/ar typed against it).
 * Wired under `i5.siteAudit`.
 */
import type { Catalog } from './fr';

const en: Catalog = {
  title: 'Site audit',
  subtitle:
    'Landing-page CRO, multi-page technical SEO, GEO and pricing page — only for a domain you have proven you own.',
  legal:
    'We only audit your own sites: verified ownership (DNS, meta tag or attested brief), robots.txt honoured, 300 pages max, polite crawling.',
  domains: {
    title: 'Your domains',
    empty: 'No domain yet. Add your site’s domain to get started.',
    add: 'Add domain',
    placeholder: 'example.com',
    label: 'Domain',
    verified: 'Verified',
    unverified: 'Not verified',
    verifiedVia: 'Verified via {{method}}',
    remove: 'Remove',
    removeConfirmTitle: 'Remove this domain?',
    removeConfirm: 'The domain and its verification token will be deleted. Audit history is kept.',
    howTo: 'Prove you own this domain with one of these methods:',
    metaHelp: 'Add this tag to the <head> of your home page, publish, then click “Verify”.',
    dnsHelp: 'Add this TXT record to the domain’s DNS zone (propagation can take a few minutes).',
    briefHelp: 'Your product brief must use this domain as its source with ownership attested.',
    openBrief: 'Open the product brief',
    lastCheck: 'Last check: {{detail}}',
    added: 'Domain added — now verify you own it.',
    verifiedToast: 'Ownership verified.',
  },
  methods: {
    meta_tag: 'Meta tag',
    dns_txt: 'DNS TXT record',
    brief_attested: 'Attested product brief',
  },
  verify: 'Verify ({{method}})',
  run: {
    title: 'Run an audit',
    domain: 'Domain to audit',
    pickDomain: 'Pick a domain',
    maxPages: 'Max pages (1–300)',
    label: 'Label (optional)',
    start: 'Run audit',
    rerun: 'Re-run audit',
    credits: 'Cost: up to {{n}} AI credits (CRO rubric). Deterministic fallback when AI is unavailable.',
    queued: 'Audit queued…',
    running: 'Crawling — {{pages}} pages analysed',
    refusedTitle: 'Audit refused',
  },
  status: { queued: 'Queued', running: 'Running', done: 'Done', failed: 'Failed' },
  scores: {
    overall: 'Overall score',
    cro: 'CRO',
    technical: 'Technical',
    geo: 'GEO',
    pricing: 'Pricing',
    na: 'n/a',
    delta: '{{sign}}{{n}} vs previous audit',
  },
  tabs: { all: 'All', cro: 'CRO', technical: 'Technical', geo: 'GEO', pricing: 'Pricing' },
  fixes: {
    title: 'Ranked fixes',
    empty: 'No fix in this category.',
    rank: '#{{n}}',
    impact: 'Impact {{n}}/5',
    effort: 'Effort {{n}}/5',
    why: 'Why',
    how: 'How',
    evidence: 'Finding',
    urls: 'Affected pages',
    markDone: 'Mark done',
    dismiss: 'Dismiss',
    reopen: 'Reopen',
    showClosed: 'Show done / dismissed fixes',
    counts: '{{open}} open · {{done}} done · {{dismissed}} dismissed',
    statusDone: 'Done',
    statusDismissed: 'Dismissed',
    updated: 'Fix updated.',
  },
  prompt: {
    title: 'Copy as prompt',
    tool: 'Tool',
    copy: 'Copy as prompt',
    copied: 'Prompt copied ({{n}} open fixes, in rank order).',
    copyFailed: 'Copy failed — select the text below.',
    preview: 'Exported prompt',
    tools: { cursor: 'Cursor', lovable: 'Lovable', bolt: 'Bolt', 'claude-code': 'Claude Code' },
  },
  summary: {
    pages: '{{n}} pages crawled',
    sitemap: 'Sitemap: {{state}}',
    robots: 'robots.txt: {{state}}',
    llms: 'llms.txt: {{state}}',
    found: 'found',
    missing: 'missing',
    crawlMode: 'Mode: {{mode}}',
    croFallback: 'Deterministic CRO rubric used (AI unavailable: {{reason}}). No AI verdict was invented.',
    croLlm: 'CRO rubric evaluated by AI (≈ {{n}} credits).',
    pricingUrl: 'Pricing page analysed: {{url}}',
    noPricing: 'No pricing page detected.',
  },
  history: {
    title: 'History',
    empty: 'No audit yet.',
    date: 'Date',
    domain: 'Domain',
    status: 'Status',
    score: 'Score',
    delta: 'Change',
    fixes: 'Fixes',
    open: 'Open',
    remove: 'Delete',
    removeConfirmTitle: 'Delete this audit?',
    removeConfirm: 'The audit and its fixes will be permanently deleted.',
    removed: 'Audit deleted.',
  },
  readOnly: 'Read-only: your role cannot run audits or change fixes.',
  unavailable: 'Site audit is not available on this environment yet.',
  loadError: 'Could not load audits.',
  genericError: 'Something went wrong. Please retry.',
};

export default en;
